import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks

export default function CadastroPdfPage() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [processingIds, setProcessingIds] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cadastro-pdf/history`);
      setHistory(res.data);
      return res.data;
    } catch (e) { return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Poll for processing items
  useEffect(() => {
    if (processingIds.length === 0) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      const data = await fetchHistory();
      const still = processingIds.filter(id => data.some(h => h.id === id && h.status === 'processing'));
      if (still.length === 0) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setProcessingIds([]);
        for (const id of processingIds) {
          const item = data.find(h => h.id === id);
          if (item?.status === 'completed') toast.success(`Client "${item.client_name}" created!`);
          else if (item?.status === 'duplicate') toast.error(`Duplicate: ${item.error}`);
          else if (item?.status === 'error') toast.error(`Error: ${item.error}`);
        }
      }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [processingIds, fetchHistory]);

  // Chunked upload — sends file in 2MB pieces to bypass proxy limits
  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadId = null;

    for (let i = 0; i < totalChunks; i++) {
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      setUploadStatus(`Uploading chunk ${i + 1} of ${totalChunks}...`);

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const slice = file.slice(start, end);

      // Convert to base64
      const arrayBuffer = await slice.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      const res = await axios.post(`${API_URL}/api/cadastro-pdf/upload-chunk`, {
        upload_id: uploadId,
        chunk_index: i,
        total_chunks: totalChunks,
        filename: file.name,
        data: base64
      }, { timeout: 60000 });

      if (i === 0) uploadId = res.data.upload_id;

      if (res.data.complete) {
        return res.data;
      }
    }
    return null;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fname = file.name.toLowerCase();
    if (!fname.endsWith('.pdf') && !fname.endsWith('.zip')) {
      toast.error(`File rejected: "${file.name}" is not a PDF or ZIP file`);
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 100 MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Starting upload...');

    try {
      let result;

      // Try normal upload first (works for smaller files and desktop)
      try {
        setUploadStatus('Uploading file...');
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API_URL}/api/cadastro-pdf/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          onUploadProgress: (ev) => {
            if (ev.total) setUploadProgress(Math.round((ev.loaded * 100) / ev.total));
          }
        });
        result = res.data;
      } catch (directErr) {
        const status = directErr?.response?.status;
        const detail = directErr?.response?.data?.detail;

        // If proxy rejected (413, 502, 504, network error), fall back to chunked upload
        if (!directErr.response || status === 413 || status === 502 || status === 504 || status === 0) {
          setUploadStatus('Direct upload blocked by proxy. Switching to chunked upload...');
          setUploadProgress(0);
          toast.info('Switching to chunked upload mode...');
          result = await uploadChunked(file);
          if (!result) throw new Error('Chunked upload failed to complete');
        } else {
          // Real backend error — show it
          throw new Error(detail || `Upload failed (HTTP ${status})`);
        }
      }

      // Process results
      const imports = result.imports || [];
      const ids = imports.filter(i => i.id && i.status === 'processing').map(i => i.id);

      if (ids.length > 0) {
        setProcessingIds(ids);
        toast.success(`${result.total || ids.length} PDF(s) uploaded — processing in background`);
      }

      // Show immediate errors
      for (const imp of imports.filter(i => i.status === 'error')) {
        toast.error(`${imp.filename}: ${imp.error}`);
      }
      if (result.error) toast.error(result.error);

      fetchHistory();
    } catch (err) {
      const msg = err.message || 'Upload failed — unknown error';
      toast.error(msg);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      e.target.value = '';
    }
  };

  const handleRetry = async (importId) => {
    setRetrying(importId);
    try {
      await axios.post(`${API_URL}/api/cadastro-pdf/retry/${importId}`);
      setProcessingIds(prev => [...prev, importId]);
      toast.success('Retrying...');
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    } finally { setRetrying(null); }
  };

  const statusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-100 text-emerald-800',
      processing: 'bg-blue-100 text-blue-800',
      error: 'bg-rose-100 text-rose-800',
      duplicate: 'bg-amber-100 text-amber-800',
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>
        {status === 'processing' && <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />}
        {status}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cadastro-pdf-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cadastro by PDF</h1>
          <p className="text-sm text-slate-500 mt-1">Upload client PDFs or ZIPs to register them automatically</p>
        </div>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Upload Client PDF or ZIP</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-8 hover:border-slate-400 transition-colors">
              {uploading ? (
                <div className="flex flex-col items-center gap-3 w-full max-w-sm">
                  <Loader2 className="w-8 h-8 text-[#1a2e5a] animate-spin" />
                  <p className="text-sm font-medium text-slate-700">{uploadStatus || `Uploading... ${uploadProgress}%`}</p>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div className="bg-[#1a2e5a] h-3 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} data-testid="upload-progress-bar" />
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-400 mb-3" />
                  <p className="text-sm text-slate-600 mb-1">Select a client PDF or ZIP file</p>
                  <p className="text-xs text-slate-400 mb-3">Maximum file size: 100 MB. Works from iPhone, Android, and desktop.</p>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed,application/octet-stream" onChange={handleUpload} className="hidden" data-testid="pdf-upload-input" />
                    <span className="inline-flex items-center px-5 py-2.5 bg-[#1a2e5a] text-white text-sm font-medium rounded-md hover:bg-[#15254a] transition-colors">
                      <FileText className="w-4 h-4 mr-2" /> Select PDF / ZIP
                    </span>
                  </label>
                </>
              )}
            </div>

            {processingIds.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="processing-indicator">
                {history.filter(h => processingIds.includes(h.id) && h.status === 'processing').map(h => (
                  <div key={h.id} className="p-3 rounded-lg border bg-blue-50 border-blue-200 flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <span className="font-medium">{h.filename}</span>
                      {h.progress && <span className="ml-2 text-blue-600">— {h.progress}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Import History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : history.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No PDFs uploaded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-medium text-slate-500 uppercase">Client</TableHead>
                      <TableHead className="text-xs font-medium text-slate-500 uppercase">File</TableHead>
                      <TableHead className="text-xs font-medium text-slate-500 uppercase">Date</TableHead>
                      <TableHead className="text-xs font-medium text-slate-500 uppercase">Status</TableHead>
                      <TableHead className="text-xs font-medium text-slate-500 uppercase">Details</TableHead>
                      <TableHead className="text-xs font-medium text-slate-500 uppercase w-28"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map(imp => (
                      <TableRow key={imp.id} className="hover:bg-slate-50" data-testid={`import-row-${imp.id}`}>
                        <TableCell className="font-medium text-slate-900">{imp.client_name || '-'}</TableCell>
                        <TableCell className="text-slate-600 text-sm max-w-[150px] truncate">{imp.filename}</TableCell>
                        <TableCell className="text-slate-600 text-xs whitespace-nowrap">
                          {imp.created_at ? new Date(imp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </TableCell>
                        <TableCell>{statusBadge(imp.status)}</TableCell>
                        <TableCell className="text-sm max-w-[250px]">
                          {imp.status === 'processing' && <span className="text-blue-600">{imp.progress}</span>}
                          {imp.status === 'error' && <span className="text-rose-600 text-xs">{imp.error}</span>}
                          {imp.status === 'duplicate' && <span className="text-amber-600 text-xs">{imp.error}</span>}
                          {imp.status === 'completed' && <span className="text-slate-500 text-xs">{imp.pages_processed} pgs, {imp.scores_imported} scores</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {imp.client_id && imp.status === 'completed' && (
                              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate(`/admin/clients/${imp.client_id}`)}>
                                <ExternalLink className="w-3 h-3 mr-1" /> View
                              </Button>
                            )}
                            {imp.status === 'error' && imp.has_stored_pdf && (
                              <Button variant="ghost" size="sm" className="text-xs text-blue-600" onClick={() => handleRetry(imp.id)} disabled={retrying === imp.id} data-testid={`retry-btn-${imp.id}`}>
                                {retrying === imp.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                                Retry
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
